import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { morphProductDependentSections } from '@theme/product-dependent-sections';
import { requestIdleCallback } from '@theme/utilities';

const SECTION_ID = 'section-rendering-card-product';
const CARD_PREVIEW_CACHE = new Map();
const PREFETCH_IN_FLIGHT = new Set();
const HOVER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const CARD_PREVIEW_DEBOUNCE_MS = 50;
const CARD_PREFETCH_IDLE_TIMEOUT_MS = 1500;
const CARD_PREFETCH_STAGGER_MS = 120;

/** @type {Map<string, string>} */
const productCardSlideIds = new Map();

/** @typedef {{ requestId: number, abortController?: AbortController, debounceTimer?: ReturnType<typeof setTimeout> }} CardPreviewState */

/** @type {WeakMap<HTMLElement, CardPreviewState>} */
const cardPreviewStates = new WeakMap();

/** @type {WeakSet<HTMLElement>} */
const boundPreviewCards = new WeakSet();

/**
 * @param {HTMLElement} card
 * @returns {CardPreviewState}
 */
function getCardPreviewState(card) {
  let state = cardPreviewStates.get(card);
  if (!state) {
    state = { requestId: 0 };
    cardPreviewStates.set(card, state);
  }
  return state;
}

/**
 * @param {string} url
 * @returns {string}
 */
function normalizeProductUrl(url) {
  const parsed = new URL(url, window.location.origin);
  return `${parsed.pathname}${parsed.search}`;
}

/**
 * @param {HTMLElement} card
 */
function ensureCardDefaults(card) {
  if (!card.dataset.cardDefaultUrl) {
    card.dataset.cardDefaultUrl = card.dataset.productUrl || '';
  }
  if (!card.dataset.cardDefaultProductId) {
    card.dataset.cardDefaultProductId = card.dataset.productId || '';
  }
}

/**
 * @param {HTMLElement} card
 * @param {string | undefined} productId
 */
function setSwatchPressed(card, productId) {
  if (!productId) return;

  card.querySelectorAll('color-swatch-picker [data-product-url]').forEach((el) => {
    el.setAttribute('aria-pressed', el.dataset.productId === productId ? 'true' : 'false');
  });
}

/**
 * @param {HTMLElement} card
 * @param {string | undefined} slideId
 */
function rememberCardSlide(card, slideId) {
  const productId = card.dataset.productId;
  if (productId && slideId) {
    productCardSlideIds.set(productId, slideId);
  }
}

/**
 * @param {string | undefined} productId
 * @returns {string | undefined}
 */
function getRememberedCardSlideId(productId) {
  if (!productId) return undefined;
  return productCardSlideIds.get(productId);
}

/**
 * @param {HTMLElement} card
 */
function captureCurrentCardSlide(card) {
  const slideshow = card.querySelector('[data-card-primary-link] slideshow-component');
  if (!slideshow || typeof slideshow.current !== 'number') return;

  const slide = slideshow.slides?.[slideshow.current];
  const slideId = slide?.getAttribute('slide-id');
  rememberCardSlide(card, slideId || undefined);
}

/**
 * @param {HTMLElement} slideshow
 * @param {string | undefined} productId
 */
function setInitialSlideFromMemory(slideshow, productId) {
  const rememberedSlideId = getRememberedCardSlideId(productId);
  if (!rememberedSlideId) return;

  const slides = slideshow.querySelectorAll('slideshow-slide');
  slides.forEach((slide, index) => {
    if (slide.getAttribute('slide-id') === rememberedSlideId) {
      slideshow.setAttribute('initial-slide', String(index));
    }
  });
}

/**
 * @param {HTMLElement} card
 * @param {string} productUrl
 */
function queueCardProductPreview(card, productUrl) {
  const state = getCardPreviewState(card);
  clearTimeout(state.debounceTimer);

  const normalizedTarget = normalizeProductUrl(productUrl);
  const normalizedCurrent = card.dataset.productUrl ? normalizeProductUrl(card.dataset.productUrl) : '';

  if (normalizedTarget === normalizedCurrent) return;

  const cached = CARD_PREVIEW_CACHE.get(getCardPreviewCacheKey(card, productUrl));
  if (cached instanceof HTMLElement) {
    state.debounceTimer = setTimeout(() => {
      fetchAndApplyCardPreview(card, productUrl);
    }, 0);
    return;
  }

  state.debounceTimer = setTimeout(() => {
    fetchAndApplyCardPreview(card, productUrl);
  }, CARD_PREVIEW_DEBOUNCE_MS);
}

/**
 * @param {HTMLElement} card
 * @param {string} productUrl
 * @param {string | undefined} productId
 */
function previewCardProduct(card, productUrl, productId) {
  ensureCardDefaults(card);
  if (productId) setSwatchPressed(card, productId);

  const defaultUrl = card.dataset.cardDefaultUrl || '';
  if (!productUrl || normalizeProductUrl(productUrl) === normalizeProductUrl(defaultUrl)) {
    restoreCardProductPreview(card);
    return;
  }

  void prefetchCardPreview(card, productUrl);
  queueCardProductPreview(card, productUrl);
}

/**
 * @param {HTMLElement} card
 */
function restoreCardProductPreview(card) {
  const state = getCardPreviewState(card);
  clearTimeout(state.debounceTimer);

  const defaultUrl = card.dataset.cardDefaultUrl;
  if (!defaultUrl) return;

  if (normalizeProductUrl(card.dataset.productUrl || '') === normalizeProductUrl(defaultUrl)) {
    setSwatchPressed(card, card.dataset.cardDefaultProductId);
    return;
  }

  setSwatchPressed(card, card.dataset.cardDefaultProductId);
  fetchAndApplyCardPreview(card, defaultUrl);
}

/**
 * @param {HTMLElement} card
 * @param {string} productUrl
 * @returns {string}
 */
function getCardPreviewCacheKey(card, productUrl) {
  return normalizeProductUrl(productUrl);
}

/**
 * @returns {string}
 */
function getCollectionHandleForCardFetch() {
  const resultsList = document.querySelector('results-list[data-collection-handle]');
  if (resultsList instanceof HTMLElement && resultsList.dataset.collectionHandle) {
    return resultsList.dataset.collectionHandle;
  }

  const productGrid = document.querySelector('[data-product-grid][data-collection-handle]');
  if (productGrid instanceof HTMLElement && productGrid.dataset.collectionHandle) {
    return productGrid.dataset.collectionHandle;
  }

  return '';
}

/**
 * @param {HTMLElement} card
 * @param {string} productUrl
 * @returns {string}
 */
function buildCardPreviewFetchUrl(card, productUrl) {
  const collectionHandle = getCollectionHandleForCardFetch();
  const productPath = new URL(productUrl, window.location.origin).pathname;
  const productHandleMatch = productPath.match(/\/products\/([^/?#]+)/);

  const url =
    collectionHandle && productHandleMatch
      ? new URL(`/collections/${collectionHandle}/products/${productHandleMatch[1]}`, window.location.origin)
      : new URL(productUrl, window.location.origin);

  url.searchParams.set('section_id', SECTION_ID);
  return url.href;
}

/**
 * @param {HTMLElement} cardElement
 */
function preloadCardPreviewImages(cardElement) {
  const images = cardElement.querySelectorAll('[data-card-primary-link] img');

  for (const img of images) {
    if (!(img instanceof HTMLImageElement)) continue;

    const src = img.currentSrc || img.src;
    if (!src) continue;

    const preload = new Image();
    preload.decoding = 'async';
    if ('fetchPriority' in preload) {
      preload.fetchPriority = 'low';
    }
    preload.src = src;

    const srcset = img.getAttribute('srcset');
    if (srcset) {
      preload.srcset = srcset;
    }
  }
}

/**
 * @param {string} html
 * @param {string} cacheKey
 * @returns {HTMLElement | null}
 */
function parseAndCacheCardPreview(html, cacheKey) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const newCard = parsed.querySelector('card-product-component');
  if (!newCard) return null;

  const clone = /** @type {HTMLElement} */ (newCard.cloneNode(true));
  CARD_PREVIEW_CACHE.set(cacheKey, clone);
  preloadCardPreviewImages(clone);
  return clone;
}

/**
 * @param {HTMLElement} card
 * @returns {string[]}
 */
function getCardVariantUrls(card) {
  const defaultUrl = normalizeProductUrl(card.dataset.cardDefaultUrl || card.dataset.productUrl || '');
  const urls = new Set();

  card.querySelectorAll('[data-product-url]').forEach((el) => {
    const productUrl = el.dataset.productUrl;
    if (!productUrl) return;
    if (normalizeProductUrl(productUrl) === defaultUrl) return;
    urls.add(productUrl);
  });

  return [...urls];
}

/**
 * @param {HTMLElement} card
 * @param {string} productUrl
 * @returns {Promise<void>}
 */
function prefetchCardPreview(card, productUrl) {
  const cacheKey = getCardPreviewCacheKey(card, productUrl);
  if (CARD_PREVIEW_CACHE.has(cacheKey) || PREFETCH_IN_FLIGHT.has(cacheKey)) {
    return Promise.resolve();
  }

  PREFETCH_IN_FLIGHT.add(cacheKey);

  return fetch(buildCardPreviewFetchUrl(card, productUrl), { priority: 'low' })
    .then((response) => response.text())
    .then((responseText) => {
      parseAndCacheCardPreview(responseText, cacheKey);
    })
    .catch(() => {})
    .finally(() => {
      PREFETCH_IN_FLIGHT.delete(cacheKey);
    });
}

/**
 * @param {HTMLElement} card
 */
function scheduleCardVariantPrefetch(card) {
  if (!window.matchMedia(HOVER_MEDIA_QUERY).matches) return;
  if (card.dataset.variantsPrefetchScheduled === 'true') return;

  card.dataset.variantsPrefetchScheduled = 'true';

  requestIdleCallback(
    () => {
      void prefetchCardVariants(card);
    },
    { timeout: CARD_PREFETCH_IDLE_TIMEOUT_MS }
  );
}

/**
 * @param {HTMLElement} card
 */
async function prefetchCardVariants(card) {
  const variantUrls = getCardVariantUrls(card);

  for (const productUrl of variantUrls) {
    await prefetchCardPreview(card, productUrl);
    await new Promise((resolve) => {
      setTimeout(resolve, CARD_PREFETCH_STAGGER_MS);
    });
  }
}

/**
 * @param {HTMLElement} card
 * @param {string} productUrl
 */
function fetchAndApplyCardPreview(card, productUrl) {
  const state = getCardPreviewState(card);
  state.requestId += 1;
  const requestId = state.requestId;

  state.abortController?.abort();
  state.abortController = new AbortController();
  const { signal } = state.abortController;

  const cacheKey = getCardPreviewCacheKey(card, productUrl);
  const cachedCard = CARD_PREVIEW_CACHE.get(cacheKey);

  if (cachedCard instanceof HTMLElement) {
    if (requestId === state.requestId) {
      applyCardPreview(card, /** @type {HTMLElement} */ (cachedCard.cloneNode(true)));
    }
    return;
  }

  card.setAttribute('aria-busy', 'true');

  fetch(buildCardPreviewFetchUrl(card, productUrl), { signal })
    .then((response) => response.text())
    .then((responseText) => {
      if (requestId !== state.requestId) return;

      const cached = parseAndCacheCardPreview(responseText, cacheKey);
      if (!cached) return;

      applyCardPreview(card, /** @type {HTMLElement} */ (cached.cloneNode(true)));
    })
    .catch((error) => {
      if (error.name !== 'AbortError') console.error(error);
    })
    .finally(() => {
      if (requestId === state.requestId) {
        card.removeAttribute('aria-busy');
      }
    });
}

/**
 * @param {HTMLElement} card
 * @param {HTMLElement} newCard
 */
function applyCardPreview(card, newCard) {
  captureCurrentCardSlide(card);

  const productId = newCard.dataset.productId;
  const primaryLink = card.querySelector('[data-card-primary-link]');
  const newPrimaryLink = newCard.querySelector('[data-card-primary-link]');
  if (primaryLink instanceof HTMLAnchorElement && newPrimaryLink instanceof HTMLAnchorElement) {
    applyPrimaryLink(primaryLink, newPrimaryLink, productId);
  }

  const infoDesktop = card.querySelector('[data-card-info-desktop]');
  const newInfoDesktop = newCard.querySelector('[data-card-info-desktop]');
  if (infoDesktop instanceof HTMLAnchorElement && newInfoDesktop instanceof HTMLAnchorElement) {
    morphLinkContent(infoDesktop, newInfoDesktop);
  }

  const infoMobile = card.querySelector('[data-card-info-mobile-link]');
  const newInfoMobile = newCard.querySelector('[data-card-info-mobile-link]');
  if (infoMobile instanceof HTMLAnchorElement && newInfoMobile instanceof HTMLAnchorElement) {
    morphLinkContent(infoMobile, newInfoMobile);
  }

  if (newCard.dataset.productUrl) {
    card.dataset.productUrl = newCard.dataset.productUrl;
  }
  if (newCard.dataset.productId) {
    card.dataset.productId = newCard.dataset.productId;
  }

  if (card.hasAttribute('data-swatches-expanded')) {
    card.querySelectorAll('color-swatch-picker').forEach((picker) => {
      if (picker instanceof ColorSwatchPicker) {
        picker.restoreExpandedState();
      }
    });
  }
}

/**
 * @param {HTMLAnchorElement} current
 * @param {HTMLAnchorElement} next
 * @param {string | undefined} productId
 */
function applyPrimaryLink(current, next, productId) {
  current.href = next.href;

  const accessibleName = next.getAttribute('aria-label');
  if (accessibleName) {
    current.setAttribute('aria-label', accessibleName);
  } else {
    current.removeAttribute('aria-label');
  }

  const oldSlideshow = current.querySelector('slideshow-component');
  const newSlideshow = next.querySelector('slideshow-component');

  if (oldSlideshow && newSlideshow) {
    const slideshowClone = /** @type {HTMLElement} */ (newSlideshow.cloneNode(true));
    setInitialSlideFromMemory(slideshowClone, productId);
    oldSlideshow.replaceWith(slideshowClone);
    return;
  }

  morph(current, next, { ...MORPH_OPTIONS, childrenOnly: true });
}

/**
 * @param {HTMLAnchorElement} current
 * @param {HTMLAnchorElement} next
 */
function morphLinkContent(current, next) {
  current.href = next.href;
  morph(current, next, { ...MORPH_OPTIONS, childrenOnly: true });
}

/**
 * @param {HTMLElement} card
 */
function bindCardProductPreview(card) {
  if (boundPreviewCards.has(card)) return;
  boundPreviewCards.add(card);
  ensureCardDefaults(card);

  card.addEventListener('pointerover', handleCardPointerOver);
  card.addEventListener('pointerleave', handleCardPointerLeave);
  card.addEventListener(SlideshowSelectEvent.eventName, handleCardSlideshowSelect);
}

/**
 * @param {Event} event
 */
function handleCardSlideshowSelect(event) {
  if (!(event instanceof SlideshowSelectEvent)) return;
  if (!event.detail.userInitiated || !event.detail.id) return;

  const card = event.currentTarget;
  if (!(card instanceof HTMLElement)) return;

  rememberCardSlide(card, event.detail.id);
}

/**
 * @param {PointerEvent} event
 */
function handleCardPointerOver(event) {
  if (!window.matchMedia(HOVER_MEDIA_QUERY).matches) return;
  if (!(event.currentTarget instanceof HTMLElement)) return;

  const card = event.currentTarget;
  scheduleCardVariantPrefetch(card);

  const swatch = event.target instanceof Element ? event.target.closest('[data-product-url]') : null;
  if (!swatch || !(swatch instanceof HTMLElement) || !card.contains(swatch)) return;

  if (event.relatedTarget instanceof Node && swatch.contains(event.relatedTarget)) return;

  previewCardProduct(card, swatch.dataset.productUrl || '', swatch.dataset.productId);
}

/**
 * @param {PointerEvent} event
 */
function handleCardPointerLeave(event) {
  if (!(event.currentTarget instanceof HTMLElement)) return;

  const card = event.currentTarget;
  if (event.relatedTarget instanceof Node && card.contains(event.relatedTarget)) return;

  restoreCardProductPreview(card);
}

class ColorSwatchPicker extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    const card = this.closest('card-product-component');
    if (card instanceof HTMLElement) {
      bindCardProductPreview(card);
    }

    this.addEventListener('click', this.#handleClick);
    this.#syncExpandedStateFromCard();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.#abortController?.abort();
  }

  #handleClick = (event) => {
    const expandButton = event.target.closest('[data-expand-swatches]');
    if (expandButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#expandSwatches(expandButton);
      return;
    }

    const swatch = event.target.closest('[data-product-url]');
    if (!swatch || !(swatch instanceof HTMLElement)) return;

    const productUrl = swatch.dataset.productUrl;
    const productInformation = this.closest('.product-information');
    const card = this.closest('card-product-component');
    if (!productUrl || (!card && !productInformation)) return;

    if (productInformation instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      this.querySelectorAll('[data-product-url]').forEach((el) => {
        el.setAttribute('aria-pressed', el === swatch ? 'true' : 'false');
      });

      this.#fetchAndMorphProductInformation(productInformation, productUrl);
      return;
    }

    if (card) {
      event.preventDefault();
      event.stopPropagation();
      window.location.assign(productUrl);
    }
  };

  /**
   * @param {HTMLButtonElement} expandButton
   */
  #expandSwatches(expandButton) {
    this.querySelectorAll('[data-swatch-overflow]').forEach((item) => {
      item.hidden = false;
    });

    expandButton.hidden = true;
    expandButton.setAttribute('aria-expanded', 'true');
    this.dataset.expanded = '';

    const card = this.closest('card-product-component');
    if (card) {
      card.dataset.swatchesExpanded = '';
    }
  }

  restoreExpandedState() {
    this.#syncExpandedStateFromCard();
  }

  #syncExpandedStateFromCard() {
    const card = this.closest('card-product-component');
    if (!card?.hasAttribute('data-swatches-expanded')) return;

    const expandButton = this.querySelector('[data-expand-swatches]');
    if (!(expandButton instanceof HTMLButtonElement) || expandButton.hidden) return;

    this.#expandSwatches(expandButton);
  }

  /**
   * @param {HTMLElement} productInformation
   * @param {string} productUrl
   */
  #fetchAndMorphProductInformation(productInformation, productUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const sectionId = productInformation.id;
    const url = new URL(productUrl, window.location.origin);
    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }

    productInformation.setAttribute('aria-busy', 'true');

    fetch(url.href, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newProductInformation = html.querySelector('.product-information');
        if (!newProductInformation) return;

        const galleryTemplate = newProductInformation.querySelector('media-gallery');
        const galleryReplacement = galleryTemplate ? galleryTemplate.cloneNode(true) : null;
        const sizePanelWasOpen =
          productInformation.querySelector('pdp-mobile-sticky-bar')?.dataset.sizePanelOpen === 'true';

        morph(productInformation, newProductInformation, MORPH_OPTIONS);

        if (sizePanelWasOpen) {
          const stickyBar = productInformation.querySelector('pdp-mobile-sticky-bar');
          if (stickyBar && typeof stickyBar.restoreOpenSizePanel === 'function') {
            stickyBar.restoreOpenSizePanel();
          }
        }

        if (galleryReplacement) {
          const currentMediaGallery = productInformation.querySelector('media-gallery');

          if (currentMediaGallery) {
            const openZoomDialog = currentMediaGallery.querySelector('dialog[open]');
            if (openZoomDialog instanceof HTMLDialogElement) {
              openZoomDialog.close();
            }

            currentMediaGallery.replaceWith(galleryReplacement);
          }
        }

        const nextUrl = new URL(productUrl, window.location.origin);
        if (nextUrl.href !== window.location.href) {
          history.replaceState({}, '', nextUrl.toString());
        }

        morphProductDependentSections(productUrl, { signal: this.#abortController?.signal });
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      })
      .finally(() => {
        productInformation.removeAttribute('aria-busy');
      });
  }
}

if (!customElements.get('color-swatch-picker')) {
  customElements.define('color-swatch-picker', ColorSwatchPicker);
}
